import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'utils/sanitizeHtml';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button, Upload, Form, Input as AntDInput, Alert, ConfigProvider } from 'antd';
import DefaultLayout from 'components/DefaultLayout';
import { connect } from 'react-redux';
import Spinner from 'components/Spinner';
import moment from 'moment';
import { DatePicker, Input } from '../Form';
import { config } from 'config/config';
import { useCertificate } from 'hooks/Certificates/useCertificate';
import SubmissionDetail from '../AssignmentActivity/SubmissionDetail';
import FileDownload from 'js-file-download';
import coursesApiClient from 'utils/coursesApiClient';
import useHandleError from 'utils/useHandleError';

interface CertificateActivityInterface {
    programActivityAttemptId: number;
}

const formLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const mapStateToProps = (state: any) => ({
    session: state.session,
});

const CertificateActivity: React.FC<CertificateActivityInterface> = ({
    programActivityAttemptId,
}) => {
    const { saveSubmission, getCertificate, loading } = useCertificate();
    const [certificate, setCertificate] = useState<any>();
    const [files, setFiles] = useState<any>([]);
    const [disable, setDisable] = useState(false);

    const intl = useIntl();
    const [form] = Form.useForm();
    const { TextArea } = AntDInput;
    const [handleError] = useHandleError();

    useEffect(() => {
        loadCertificate();
    }, []);

    // 24.3.10: the student must be able to retrieve the attachments of a
    // returned submission. Server-side files are listed as {uid: storage_id,
    // name}; a file just picked in this session still has originFileObj.
    const handleDownload = async (file: any) => {
        try {
            if (file?.originFileObj) {
                return FileDownload(file.originFileObj, file.name);
            }
            const response = await coursesApiClient.request(`/api/v1/courses/storage-file/${file.uid}`, [], 'GET', true, true);
            FileDownload(response, file.name);
        } catch (error) {
            handleError(error);
        }
    };
    const loadCertificate = async () => {
        let response = await getCertificate(programActivityAttemptId);

        if (response.data?.submission) {
            setFormData(response);
            response.data.submission.deadline = response.data.deadline;

            if (response.data.submission.status !== 'NOT_SUBMITTED') {
                response.data.submission.submission_date = response.data.submission.created_at;
            }
        }

        let normalized = normalizeCertificate(response.data);
        setCertificate(normalized);
    };

    const setFormData = (response: any) => {
        // 24.3.10: a RETURNED submission may be corrected and re-submitted
        if (!['NOT_SUBMITTED', 'RETURNED'].includes(response.data.submission.status)) {
            setDisable(true);
        } else {
            setDisable(false);
        }

        form.setFieldsValue({
            ...response.data.submission,
            issue_date: response.data.submission.issue_date
                ? moment(response.data.submission.issue_date)
                : null,
            expire_date: response.data.submission.expire_date
                ? moment(response.data.submission.expire_date)
                : null,
        });
        const storedFiles = response.data.files ?? [];
        setFiles(storedFiles);
        form.setFieldsValue({ files: { fileList: storedFiles } });
    };

    const fileUploadAllowed = (cert: any) => {
        if (!cert?.settings) {
            return false;
        }
        const settings =
            typeof cert.settings === 'string' ? JSON.parse(cert.settings) : cert.settings;

        return !!settings.fileUpload || !!settings.file_upload;
    };

    const submitForm = async (values: any) => {
        let bodyFormData = new FormData();

        Object.entries(values).map((value: any) => {
            if (value[0] === 'files' && value[1]) {
                const fileList = value[1].fileList;
                Object.entries(fileList)
                    .filter((file: any) => !!file[1].originFileObj)
                    .forEach((file: any) => {
                        bodyFormData.append(`file_${file[0]}`, file[1].originFileObj);
                    });

                const keptFileIds = Object.values(fileList)
                    .filter((file: any) => !file.originFileObj && file.uid)
                    .map((file: any) => file.uid);

                bodyFormData.append('keep_files', keptFileIds.join(','));
            }
            if (moment.isMoment(value[1])) {
                bodyFormData.append(value[0], moment(value[1]).format(config.DBDateFormat));
            } else if (!!value[1]) {
                bodyFormData.append(value[0], value[1]);
            }
        });

        const response = await saveSubmission(bodyFormData, programActivityAttemptId);

        if (response.data?.submission) {
            response.data.submission.deadline = response.data.deadline;
            response.data.submission.submission_date = response.data.submission.created_at;
            setFormData(response);
            setCertificate(response.data);
        }
    };

    const normalizeCertificate = (raw: any) => {
        let settings = raw?.settings ?? {};
        if (typeof settings === 'string') {
            try {
                settings = JSON.parse(settings);
            } catch (e) {
                settings = {};
            }
        }

        let fileUpload = settings.fileUpload ?? settings.file_upload ?? false;
        let certificateValidation = settings.certificateValidation ?? settings.certificate_validation ?? false;

        return {
            ...raw,
            settings: {
                title: settings.title ?? '',
                description: settings.description ?? '',
                fileUpload,
                certificateValidation,
            },
        };
    };

    return (
        <Spinner spinning={loading}>
            {certificate && (
                <DefaultLayout.PageLayout withoutPageHeader>
                    <div className="assignment-form-wrapper">
                        <DefaultLayout.PageContent>
                            <h1 className={'flex justify-center'}>{certificate.settings.title}</h1>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(
                                        certificate && certificate.settings.description
                                            ? certificate.settings.description
                                            : ''
                                    ),
                                }}
                            />
                            <div className={'flex'}>
                                <div className={'bold'}>
                                    <FormattedMessage id={'general.deadline'} />:
                                </div>
                                {certificate.deadline}
                            </div>
                            {certificate?.submission?.status === 'RETURNED' && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: '15px' }}
                                    message={intl.formatMessage({
                                        id: 'courses.certificate_returned_explaination',
                                    })}
                                />
                            )}
                            <Form
                                disabled={disable}
                                form={form}
                                onFinish={submitForm}
                                {...formLayout}
                            >
                                <span className={'hidden'}>
                                    <Input
                                        name="id"
                                        label={intl.formatMessage({ id: 'general.title' })}
                                    />
                                </span>
                                <Input
                                    name="title"
                                    label={intl.formatMessage({ id: 'general.title' })}
                                    customRules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'validation.field_required',
                                            }),
                                        },
                                    ]}
                                />

                                <DatePicker
                                    label={intl.formatMessage({ id: 'courses.issue_date' })}
                                    customRules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'validation.field_required',
                                            }),
                                        },
                                    ]}
                                    name={'issue_date'}
                                    defaultValue={null}
                                    format={config.defaultDateFormat}
                                />
                                <DatePicker
                                    label={intl.formatMessage({ id: 'courses.expire_date' })}
                                    name={'expire_date'}
                                    defaultValue={null}
                                    format={config.defaultDateFormat}
                                />
                                {fileUploadAllowed(certificate) ? (
                                    // A disabled Form propagates through antd's DisabledContext into
                                    // the upload list's own action buttons, which kills the download
                                    // icon. Reset the context here and disable the upload trigger by
                                    // hand, so a read-only submission stays downloadable. The Upload
                                    // must remain Form.Item's direct child - that is what receives
                                    // the injected value/onChange.
                                    <ConfigProvider componentDisabled={false}>
                                        <Form.Item
                                            label={intl.formatMessage({ id: 'general.upload_files' })}
                                            name="files"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: intl.formatMessage({
                                                        id: 'validation.field_required',
                                                    }),
                                                },
                                            ]}
                                        >
                                            <Upload
                                                key={files.map((file: any) => file.uid).join('|')}
                                                disabled={disable}
                                                beforeUpload={() => false}
                                                listType="picture"
                                                isImageUrl={() => false}
                                                defaultFileList={files}
                                                onDownload={handleDownload}
                                                showUploadList={{ showDownloadIcon: true, showRemoveIcon: !disable }}
                                            >
                                                <Button disabled={disable}>
                                                    <FormattedMessage id={'general.upload'} />
                                                </Button>
                                            </Upload>
                                        </Form.Item>
                                    </ConfigProvider>
                                ) : null}
                                <Form.Item
                                    name="comment"
                                    label={intl.formatMessage({
                                        id: 'courses.certificate_comment',
                                    })}
                                >
                                    <TextArea
                                        autoSize={{ minRows: 5, maxRows: 10 }}
                                        showCount
                                        maxLength={255}
                                    />
                                </Form.Item>
                                {certificate?.submission && (
                                    <SubmissionDetail submission={certificate.submission} />
                                )}
                            </Form>
                            {!disable && (
                                <div className={'flex justify-end mt-5'}>
                                    <Button type={'primary'} onClick={() => form.submit()}>
                                        <FormattedMessage id={'general.submit'} />
                                    </Button>
                                </div>
                            )}
                        </DefaultLayout.PageContent>
                    </div>
                </DefaultLayout.PageLayout>
            )}
        </Spinner>
    );
};

export default connect(mapStateToProps)(CertificateActivity);
